/**
 * ログ — アクセス / ユーザー操作 / 管理者操作 / 同期 / 一括登録 / ポリシー判定。
 *
 * ログの実体は 2 系統ある(`server/app/idp/README.md`)。Keycloak のイベントを
 * 60 秒ごとに写したもの(アクセス・ユーザー操作・管理者操作の Keycloak ぶん)と、
 * スイート自身の記録(監査・突合の履歴・一括登録ジョブ)。
 *
 * 期間はこの画面で 1 つだけ持ち、どのサブタブも同じ期間を見る。絞り込みは
 * サブタブごと。CSV は**画面と同じ絞り込み**をそのままサーバーに渡す。
 */
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Flex,
  Input,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import type { TablePaginationConfig } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

import type {
  BulkJob,
  BulkRow,
  LogAdminOp,
  LogEvent,
  LogSync,
  PolicyDecisionRow,
} from "../../lib/api";
import { jst } from "../../lib/format";
import { errorText, useAnyAdmin, useIdpPerms } from "./hooks";
import {
  logParams,
  useLogAccess,
  useLogAdminOps,
  useLogBulk,
  useLogBulkJob,
  useLogCsv,
  useLogPull,
  useLogStatus,
  useLogSync,
  useLogUserOps,
} from "./logHooks";
import {
  ACCESS_TYPE_OPTIONS,
  BULK_STATUS_OPTIONS,
  DEFAULT_RANGE_DAYS,
  LOG_FAILURE,
  LOG_TABS,
  RANGE_PRESETS,
  RESULT_OPTIONS,
  SOURCE_OPTIONS,
  SYNC_TASK_OPTIONS,
  USER_OPS_TYPE_OPTIONS,
  lastDays,
  rangeParams,
  type LogTabKey,
  type Range,
} from "./logOptions";
import { DECISION_OPTIONS, decisionColor } from "./policyOptions";
import { usePolicyDecisions } from "./policyHooks";
import { ROW_ERROR } from "./userOptions";

const PAGE_SIZES = [20, 50, 100];

/** 一覧 1 つぶんの状態(ページと、実際に問い合わせに使う絞り込み)。 */
function useListState<F extends Record<string, string>>(initial: F) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [draft, setDraft] = useState<F>(initial);
  const [applied, setApplied] = useState<F>(initial);

  return {
    page,
    pageSize,
    draft,
    applied,
    set: (k: keyof F, v: string) => setDraft({ ...draft, [k]: v } as F),
    search: () => {
      setPage(1);
      setApplied(draft);
    },
    clear: () => {
      setPage(1);
      setDraft(initial);
      setApplied(initial);
    },
    onPage: (p: number, s: number) => {
      setPage(p);
      setPageSize(s);
    },
  };
}

/** ページングの設定(5 つの表で同じ形)。 */
function pager(
  page: number,
  pageSize: number,
  total: number | undefined,
  onPage: (p: number, s: number) => void,
): TablePaginationConfig {
  return {
    current: page,
    pageSize,
    total: total ?? 0,
    showSizeChanger: true,
    pageSizeOptions: PAGE_SIZES,
    showTotal: (t: number) => `全 ${t} 件`,
    onChange: onPage,
  };
}

function ErrorBox({ error, what }: { error: unknown; what: string }) {
  if (!error) return null;
  return (
    <Alert
      type="error"
      showIcon
      style={{ marginBottom: 12 }}
      message={`${what}を取得できなかった`}
      description={errorText(error)}
    />
  );
}

function ResultTag({ result, label }: { result: string; label: string }) {
  return <Tag color={result === LOG_FAILURE ? "red" : "green"}>{label || result}</Tag>;
}

/** 絞り込みの箱。右端に CSV。 */
function FilterBar({
  children,
  onSearch,
  onClear,
  onCsv,
}: {
  children: ReactNode;
  onSearch: () => void;
  onClear: () => void;
  onCsv: () => void;
}) {
  return (
    <Card size="small" style={{ marginBottom: 12 }}>
      <Flex gap={8} wrap align="center">
        {children}
        <Space>
          <Button type="primary" onClick={onSearch}>
            絞り込む
          </Button>
          <Button onClick={onClear}>解除</Button>
        </Space>
        <div style={{ flex: 1 }} />
        <Button icon={<DownloadOutlined />} onClick={onCsv}>
          CSV
        </Button>
      </Flex>
    </Card>
  );
}

// ---- 期間と取込状態 ---------------------------------------------------------------

function PeriodBar({ range, onChange }: { range: Range; onChange: (r: Range) => void }) {
  return (
    <Card size="small" style={{ marginBottom: 12 }}>
      <Flex gap={8} wrap align="center">
        <span>期間</span>
        <DatePicker.RangePicker
          showTime
          allowClear={false}
          value={range}
          onChange={(v) => v && v[0] && v[1] && onChange([v[0], v[1]])}
          disabledDate={(d) => d.isAfter(lastDays(1)[1], "day")}
        />
        <Space>
          {RANGE_PRESETS.map((p) => (
            <Button key={p.days} size="small" onClick={() => onChange(lastDays(p.days))}>
              {p.label}
            </Button>
          ))}
        </Space>
      </Flex>
    </Card>
  );
}

/** 取込の状態。Keycloak 側の記録が止まっていれば目立たせる。 */
function IngestBanner() {
  const { canAdmin } = useIdpPerms();
  const status = useLogStatus();
  const pull = useLogPull();
  const s = status.data;
  const kc = s?.keycloak;
  const stopped = s && (!s.enabled || (kc?.known && !kc.eventsEnabled));

  return (
    <Card size="small" style={{ marginBottom: 12 }}>
      <Flex gap={16} wrap align="center" justify="space-between">
        <Descriptions
          size="small"
          column={4}
          style={{ flex: 1, minWidth: 320 }}
          items={[
            { key: "last", label: "最後の取込", children: jst(s?.lastImportAt) || "まだ無い" },
            {
              key: "kc",
              label: "Keycloak のイベント記録",
              children: !kc?.known ? (
                <Tag>不明</Tag>
              ) : kc.eventsEnabled ? (
                <Tag color="green">有効(保持 {kc.eventsExpirationDays} 日)</Tag>
              ) : (
                <Tag color="red">無効</Tag>
              ),
            },
            {
              key: "counts",
              label: "写したイベント",
              children: `${(s?.counts?.login ?? 0) + (s?.counts?.user ?? 0) + (s?.counts?.admin ?? 0)} 件`,
            },
            { key: "keep", label: "スイート側の保持", children: `${s?.retentionDays ?? "—"} 日` },
          ]}
        />
        {canAdmin && (
          <Button
            icon={<ReloadOutlined />}
            loading={pull.isPending}
            onClick={() => pull.mutate()}
          >
            いま取り込む
          </Button>
        )}
      </Flex>
      {stopped && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message={s?.enabled ? "Keycloak がイベントを記録していない" : "ログの取込を止めている"}
          description={
            s?.enabled
              ? "設定を確かめるジョブ(起動の 5 分後と 1 日 1 回)が自動的に有効化する。idp.logs.manage_kc_config を false にしている場合は、Keycloak 管理コンソールの Realm settings → Sessions/Events で有効にする。"
              : "設定 idp.logs.enabled が false。有効にすると 60 秒ごとに取り込む。"
          }
        />
      )}
      <ErrorBox error={status.error} what="取込の状態" />
    </Card>
  );
}

// ---- アクセスログ / ユーザー操作ログ(Keycloak のイベント)------------------------

const eventColumnsBase: ColumnsType<LogEvent> = [
  { title: "日時", dataIndex: "occurredAt", width: 180, render: (v: string) => jst(v) },
  { title: "ユーザー名", dataIndex: "username", width: 200, },
  { title: "IP アドレス", dataIndex: "ip", width: 360 },
  { title: "種別", dataIndex: "eventTypeLabel", width: 200, },
  {
    title: "結果",
    dataIndex: "resultLabel",
    width: 110,
    render: (v: string, row: LogEvent) => <ResultTag result={row.result} label={v} />,
  },
  {
    title: "理由",
    onCell: (row) => ({ title: row.error || row.details?.reason || "" }),
    dataIndex: "error",
    width: 320,
    ellipsis: true,
    render: (v: string, row: LogEvent) => v || row.details?.reason || "",
  },
];

function AccessTab({ range, initial }: { range: Range; initial: { username: string; userId: string } }) {
  const csv = useLogCsv();
  const st = useListState({
    username: initial.username,
    userId: initial.userId,
    ip: "",
    eventType: "",
    result: "",
  });
  const { page, pageSize, applied } = st;
  const params = useMemo(
    () =>
      logParams({
        ...rangeParams(range),
        limit: pageSize,
        offset: (page - 1) * pageSize,
        username: applied.username,
        user_id: applied.userId,
        ip: applied.ip,
        event_type: applied.eventType,
        result: applied.result,
      }),
    [range, page, pageSize, applied],
  );
  const query = useLogAccess(params);

  const columns: ColumnsType<LogEvent> = [
    ...eventColumnsBase,
    { title: "SSO TARGET", width: 240, dataIndex: "target", },
  ];

  return (
    <>
      <FilterBar
        onSearch={st.search}
        onClear={st.clear}
        onCsv={() => csv("access", params)}
      >
        <Input
          placeholder="ユーザー名(部分一致)"
          value={st.draft.username}
          onChange={(e) => st.set("username", e.target.value)}
          onPressEnter={st.search}
          style={{ width: 200 }}
          allowClear
        />
        <Input
          placeholder="IP アドレス"
          value={st.draft.ip}
          onChange={(e) => st.set("ip", e.target.value)}
          onPressEnter={st.search}
          style={{ width: 160 }}
          allowClear
        />
        <Select
          placeholder="種別"
          value={st.draft.eventType || undefined}
          onChange={(v) => st.set("eventType", v ?? "")}
          options={ACCESS_TYPE_OPTIONS}
          style={{ width: 220 }}
          allowClear
        />
        <Select
          placeholder="結果"
          value={st.draft.result || undefined}
          onChange={(v) => st.set("result", v ?? "")}
          options={RESULT_OPTIONS}
          style={{ width: 120 }}
          allowClear
        />
        {applied.userId && <Tag color="blue">1 人に絞り込み中</Tag>}
      </FilterBar>
      <ErrorBox error={query.error} what="アクセスログ" />
      <Table<LogEvent>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        locale={{ emptyText: "この期間のログイン記録は無い" }}
        pagination={pager(page, pageSize, query.data?.total, st.onPage)}
      />
    </>
  );
}

function UserOpsTab({ range }: { range: Range }) {
  const csv = useLogCsv();
  const st = useListState({ username: "", ip: "", eventType: "", result: "" });
  const { page, pageSize, applied } = st;
  const params = useMemo(
    () =>
      logParams({
        ...rangeParams(range),
        limit: pageSize,
        offset: (page - 1) * pageSize,
        username: applied.username,
        ip: applied.ip,
        event_type: applied.eventType,
        result: applied.result,
      }),
    [range, page, pageSize, applied],
  );
  const query = useLogUserOps(params);

  return (
    <>
      <FilterBar
        onSearch={st.search}
        onClear={st.clear}
        onCsv={() => csv("user-ops", params)}
      >
        <Input
          placeholder="ユーザー名(部分一致)"
          value={st.draft.username}
          onChange={(e) => st.set("username", e.target.value)}
          onPressEnter={st.search}
          style={{ width: 200 }}
          allowClear
        />
        <Input
          placeholder="IP アドレス"
          value={st.draft.ip}
          onChange={(e) => st.set("ip", e.target.value)}
          onPressEnter={st.search}
          style={{ width: 160 }}
          allowClear
        />
        <Select
          placeholder="種別"
          value={st.draft.eventType || undefined}
          onChange={(v) => st.set("eventType", v ?? "")}
          options={USER_OPS_TYPE_OPTIONS}
          style={{ width: 260 }}
          allowClear
        />
        <Select
          placeholder="結果"
          value={st.draft.result || undefined}
          onChange={(v) => st.set("result", v ?? "")}
          options={RESULT_OPTIONS}
          style={{ width: 120 }}
          allowClear
        />
      </FilterBar>
      <ErrorBox error={query.error} what="ユーザー操作ログ" />
      <Table<LogEvent>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        columns={eventColumnsBase}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        locale={{ emptyText: "この期間に本人による操作は無い" }}
        pagination={pager(page, pageSize, query.data?.total, st.onPage)}
      />
    </>
  );
}

// ---- 管理者操作ログ(スイートの監査 + Keycloak)------------------------------------

function AdminOpsTab({ range }: { range: Range }) {
  const csv = useLogCsv();
  const st = useListState({ actor: "", action: "", app: "", source: "" });
  const { page, pageSize, applied } = st;
  const params = useMemo(
    () =>
      logParams({
        ...rangeParams(range),
        limit: pageSize,
        offset: (page - 1) * pageSize,
        actor: applied.actor,
        action: applied.action,
        app: applied.app,
        source: applied.source,
      }),
    [range, page, pageSize, applied],
  );
  const query = useLogAdminOps(params);

  const columns: ColumnsType<LogAdminOp> = [
    { title: "日時", dataIndex: "occurredAt", width: 180, render: (v: string) => jst(v) },
    { title: "操作内容", width: 320, dataIndex: "operation", ellipsis: true },
    { title: "操作者", dataIndex: "actor", width: 160, },
    { title: "IP アドレス", dataIndex: "ip", width: 360 },
    { title: "アプリ", dataIndex: "app", width: 160 },
    {
      title: "出所",
      dataIndex: "sourceLabel",
      width: 110,
      render: (v: string, row: LogAdminOp) => (
        <Tag color={row.source === "keycloak" ? "purple" : "blue"}>{v}</Tag>
      ),
    },
    { title: "詳細", width: 320, dataIndex: "detail", ellipsis: true },
  ];

  return (
    <>
      <Typography.Paragraph type="secondary">
        スイートの監査ログと Keycloak の管理操作を 1 つの時系列にしている。
        見えるのは自分が管理しているアプリの行だけ(`suite:admin` は全部)。
        Keycloak の管理操作は `idp:admin` と `suite:admin` にだけ出る。
      </Typography.Paragraph>
      <FilterBar
        onSearch={st.search}
        onClear={st.clear}
        onCsv={() => csv("admin-ops", params)}
      >
        <Input
          placeholder="操作者(部分一致)"
          value={st.draft.actor}
          onChange={(e) => st.set("actor", e.target.value)}
          onPressEnter={st.search}
          style={{ width: 200 }}
          allowClear
        />
        <Input
          placeholder="操作(例 idp.user.update)"
          value={st.draft.action}
          onChange={(e) => st.set("action", e.target.value)}
          onPressEnter={st.search}
          style={{ width: 240 }}
          allowClear
        />
        <Input
          placeholder="アプリ(例 idp)"
          value={st.draft.app}
          onChange={(e) => st.set("app", e.target.value)}
          onPressEnter={st.search}
          style={{ width: 140 }}
          allowClear
        />
        <Select
          placeholder="出所"
          value={st.draft.source || undefined}
          onChange={(v) => st.set("source", v ?? "")}
          options={SOURCE_OPTIONS}
          style={{ width: 140 }}
          allowClear
        />
      </FilterBar>
      <ErrorBox error={query.error} what="管理者操作ログ" />
      <Table<LogAdminOp>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        locale={{ emptyText: "この期間の管理操作は無い" }}
        pagination={pager(page, pageSize, query.data?.total, st.onPage)}
      />
    </>
  );
}

// ---- 同期ログ -----------------------------------------------------------------------

function SyncTab({ range }: { range: Range }) {
  const csv = useLogCsv();
  const st = useListState({ task: "", result: "" });
  const { page, pageSize, applied } = st;
  const params = useMemo(
    () =>
      logParams({
        ...rangeParams(range),
        limit: pageSize,
        offset: (page - 1) * pageSize,
        task: applied.task,
        result: applied.result,
      }),
    [range, page, pageSize, applied],
  );
  const query = useLogSync(params);

  const columns: ColumnsType<LogSync> = [
    { title: "日時", dataIndex: "occurredAt", width: 180, render: (v: string) => jst(v) },
    { title: "タスク", dataIndex: "task", width: 240, },
    {
      title: "結果",
      dataIndex: "resultLabel",
      width: 110,
      render: (v: string, row: LogSync) => <ResultTag result={row.result} label={v} />,
    },
    {
      title: "件数",
      dataIndex: "count",
      width: 90,
      render: (v: number | null) => (v === null ? "—" : v),
    },
    { title: "実行者", dataIndex: "actor", width: 160 },
    { title: "内容", width: 320, dataIndex: "message", ellipsis: true },
  ];

  return (
    <>
      <Typography.Paragraph type="secondary">
        自動で動くもの(アカウント突合、従業員の異動にあわせた発行・停止、退職者の匿名化、
        SaaS コネクタ)の実行履歴。
      </Typography.Paragraph>
      <FilterBar onSearch={st.search} onClear={st.clear} onCsv={() => csv("sync", params)}>
        <Select
          placeholder="タスク"
          value={st.draft.task || undefined}
          onChange={(v) => st.set("task", v ?? "")}
          options={SYNC_TASK_OPTIONS}
          style={{ width: 280 }}
          allowClear
        />
        <Select
          placeholder="結果"
          value={st.draft.result || undefined}
          onChange={(v) => st.set("result", v ?? "")}
          options={RESULT_OPTIONS}
          style={{ width: 120 }}
          allowClear
        />
      </FilterBar>
      <ErrorBox error={query.error} what="同期ログ" />
      <Table<LogSync>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        locale={{ emptyText: "この期間に自動処理は動いていない" }}
        pagination={pager(page, pageSize, query.data?.total, st.onPage)}
      />
    </>
  );
}

// ---- 一括登録ログ -------------------------------------------------------------------

const bulkRowColumns: ColumnsType<BulkRow> = [
  { title: "行", dataIndex: "line", width: 90 },
  { title: "ユーザー名", dataIndex: "username", width: 220 },
  {
    title: "結果",
    dataIndex: "result",
    width: 110,
    render: (v: string) => <Tag color={v === ROW_ERROR ? "red" : "green"}>{v}</Tag>,
  },
  { title: "内容", width: 320, ellipsis: true, dataIndex: "message" },
];

/** 行を開いたときだけ引く(一覧には行結果を入れていない)。 */
function BulkRows({ id }: { id: number }) {
  const query = useLogBulkJob(id);
  return (
    <>
      <ErrorBox error={query.error} what="行ごとの結果" />
      <Table<BulkRow>
        scroll={{ x: "max-content" }}
        rowKey="line"
        size="small"
        loading={query.isFetching}
        columns={bulkRowColumns}
        dataSource={query.data?.rows ?? []}
        locale={{ emptyText: "行の記録が無い" }}
        pagination={{ pageSize: 10, showSizeChanger: false }}
      />
    </>
  );
}

function BulkTab({ range }: { range: Range }) {
  const csv = useLogCsv();
  const st = useListState({ status: "" });
  const { page, pageSize, applied } = st;
  const params = useMemo(
    () =>
      logParams({
        ...rangeParams(range),
        limit: pageSize,
        offset: (page - 1) * pageSize,
        status: applied.status,
      }),
    [range, page, pageSize, applied],
  );
  const query = useLogBulk(params);

  const columns: ColumnsType<BulkJob> = [
    { title: "日時", dataIndex: "createdAt", width: 180, render: (v: string | null) => jst(v) },
    { title: "種類", dataIndex: "kindLabel", width: 140, render: (v: string) => <Tag>{v}</Tag> },
    { title: "状態", dataIndex: "statusLabel", width: 110 },
    { title: "ファイル", width: 160, dataIndex: "filename", },
    { title: "実行者", dataIndex: "actor", width: 160 },
    { title: "全体", dataIndex: "total", width: 90 },
    { title: "成功", dataIndex: "ok", width: 90 },
    {
      title: "失敗",
      dataIndex: "failed",
      width: 110,
      render: (v: number) => (v > 0 ? <Tag color="red">{v}</Tag> : v),
    },
    { title: "備考", width: 320, dataIndex: "message", ellipsis: true },
  ];

  return (
    <>
      <Typography.Paragraph type="secondary">
        CSV による一括登録・一括更新の履歴。行を開くと 1 行ごとの結果が出る
        (初期パスワードは残していない)。
      </Typography.Paragraph>
      <FilterBar onSearch={st.search} onClear={st.clear} onCsv={() => csv("bulk", params)}>
        <Select
          placeholder="状態"
          value={st.draft.status || undefined}
          onChange={(v) => st.set("status", v ?? "")}
          options={BULK_STATUS_OPTIONS}
          style={{ width: 160 }}
          allowClear
        />
      </FilterBar>
      <ErrorBox error={query.error} what="一括登録ログ" />
      <Table<BulkJob>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        locale={{ emptyText: "この期間に一括登録していない" }}
        expandable={{ expandedRowRender: (job) => <BulkRows id={job.id} /> }}
        pagination={pager(page, pageSize, query.data?.total, st.onPage)}
      />
    </>
  );
}

// ---- ポリシー判定(R7d)------------------------------------------------------------

/**
 * アクセスポリシーの判定のうち **記録されたもの**。
 *
 * 許可は残さない(ログインの成否はアクセスログ側にある)。ここに出るのは拒否と
 * 追加認証で、Keycloak の Authenticator が判定 API を呼べなかったときの拒否も入る。
 */
function PolicyDecisionsTab({ range }: { range: Range }) {
  const csv = useLogCsv();
  const st = useListState({ username: "", ip: "", clientId: "", decision: "" });
  const { page, pageSize, applied } = st;
  const params = useMemo(
    () =>
      logParams({
        ...rangeParams(range),
        limit: pageSize,
        offset: (page - 1) * pageSize,
        username: applied.username,
        ip: applied.ip,
        client_id: applied.clientId,
        decision: applied.decision,
      }),
    [range, page, pageSize, applied],
  );
  const query = usePolicyDecisions(params);

  const columns: ColumnsType<PolicyDecisionRow> = [
    { title: "日時", dataIndex: "occurredAt", width: 180, render: (v: string) => jst(v) },
    {
      title: "判定",
      dataIndex: "decisionLabel",
      width: 120,
      render: (v: string, row) => <Tag color={decisionColor(row.decision)}>{v || row.decision}</Tag>,
    },
    { title: "ユーザー名", dataIndex: "username", width: 180, },
    { title: "接続元 IP", dataIndex: "ip", width: 360 },
    { title: "接続先", dataIndex: "target", width: 240, },
    { title: "ポリシー", dataIndex: "groupName", width: 160, },
    {
      title: "証明書",
      dataIndex: "certPresent",
      width: 110,
      render: (v: boolean) => (v ? "あり" : "なし"),
    },
    {
      title: "OTP 登録",
      dataIndex: "otpSetup",
      width: 160,
      render: (v: boolean) => (v ? <Tag color="orange">必要</Tag> : ""),
    },
    { title: "理由", width: 320, dataIndex: "reason", ellipsis: true },
  ];

  return (
    <>
      <Typography.Paragraph type="secondary">
        拒否と追加認証だけが残る(許可は記録しない)。判定を適用していない間は、
        「ポリシー設定」の試算と Keycloak 側の判定だけが動き、ここには何も増えない。
      </Typography.Paragraph>
      <FilterBar onSearch={st.search} onClear={st.clear} onCsv={() => csv("policy", params)}>
        <Input
          placeholder="ユーザー名(部分一致)"
          value={st.draft.username}
          onChange={(e) => st.set("username", e.target.value)}
          onPressEnter={st.search}
          style={{ width: 200 }}
          allowClear
        />
        <Input
          placeholder="接続元 IP"
          value={st.draft.ip}
          onChange={(e) => st.set("ip", e.target.value)}
          onPressEnter={st.search}
          style={{ width: 160 }}
          allowClear
        />
        <Input
          placeholder="クライアント ID"
          value={st.draft.clientId}
          onChange={(e) => st.set("clientId", e.target.value)}
          onPressEnter={st.search}
          style={{ width: 200 }}
          allowClear
        />
        <Select
          placeholder="判定"
          value={st.draft.decision || undefined}
          onChange={(v) => st.set("decision", v ?? "")}
          options={DECISION_OPTIONS}
          style={{ width: 160 }}
          allowClear
        />
      </FilterBar>
      <ErrorBox error={query.error} what="ポリシー判定ログ" />
      <Table<PolicyDecisionRow>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        locale={{ emptyText: "この期間に記録された判定は無い" }}
        pagination={pager(page, pageSize, query.data?.total, st.onPage)}
        scroll={{ x: 1830 }}
      />
    </>
  );
}

// ---- 画面 ---------------------------------------------------------------------------

/**
 * 管理者操作ログだけの画面(`/idp/logs/admin-ops`)。
 *
 * ルートは `RequireAnyAdmin` の配下に置いてあるので、**idp のロールが無い
 * `<app>:admin`(hr:admin など)でも開ける**(R7c fix1 低1)。そういう人も来るので、
 * ここでは取込の状態(`/idp/logs/status` = idp:viewer 以上)を読まない。
 */
export function AdminOpsLogPage() {
  const [range, setRange] = useState<Range>(lastDays(DEFAULT_RANGE_DAYS));

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={4}>管理者操作ログ</Typography.Title>
      <PeriodBar range={range} onChange={setRange} />
      <AdminOpsTab range={range} />
    </div>
  );
}

export function LogsPage() {
  const [sp, setSp] = useSearchParams();
  // 管理者操作ログは admin だけに見せる(中身の可視性は API が決める。R7c fix1 低1)
  const anyAdmin = useAnyAdmin();
  const tabs = LOG_TABS.filter((t) => t.key !== "admin-ops" || anyAdmin);
  // 出していないタブを URL で指されたらアクセスログに戻す
  const tab = (tabs.find((t) => t.key === sp.get("tab"))?.key ?? "access") as LogTabKey;
  const [range, setRange] = useState<Range>(lastDays(DEFAULT_RANGE_DAYS));
  // ユーザー一覧の「ログ」リンクから来たとき(?userId=&username=)
  const initialUser = useMemo(
    () => ({ username: sp.get("username") ?? "", userId: sp.get("userId") ?? "" }),
    // 最初の 1 回だけ拾う(絞り込みを消したあとに URL で戻さない)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const body: Record<LogTabKey, ReactNode> = {
    access: <AccessTab range={range} initial={initialUser} />,
    "user-ops": <UserOpsTab range={range} />,
    "admin-ops": <AdminOpsTab range={range} />,
    sync: <SyncTab range={range} />,
    bulk: <BulkTab range={range} />,
    policy: <PolicyDecisionsTab range={range} />,
  };

  return (
    <div>
      <Typography.Title level={4}>ログ</Typography.Title>
      <IngestBanner />
      <PeriodBar range={range} onChange={setRange} />
      <Tabs
        activeKey={tab}
        onChange={(k) => {
          const next = new URLSearchParams(sp);
          next.set("tab", k);
          next.delete("userId");
          next.delete("username");
          setSp(next, { replace: true });
        }}
        items={tabs.map((t) => ({ key: t.key, label: t.label, children: body[t.key] }))}
        destroyInactiveTabPane
      />
    </div>
  );
}
